import type { Transition } from 'motion/react';
import {
  BLUR_PEAK_PX,
  OVERSHOOT_FRACTION,
  REBOUND_EASE,
  REBOUND_T,
  SLOT_CURVE,
  type AnimMode,
} from './readyStep.constants';

export interface SlotSpinAnimation {
  laneAnimate: { x: number | number[] };
  laneTransition: Transition;
  blurAnimate: { filter: string | string[] };
  blurTransition: Transition;
}

/**
 * Pure derivation of motion targets + transitions for the slot lane.
 * Pulled out of `ReadyStep` so the component body stays focused on
 * state-machine wiring and JSX.
 *
 * Geometry:
 *   • The lane overshoots by `OVERSHOOT_FRACTION` of a tile, then
 *     snaps back to `finalX` — the same slot-machine "rebound" feel
 *     real reels have.
 *   • Outside a live spin the lane rests at `finalX` while the
 *     neighbours are dissolving (`cleared` / `reveal` phases), so
 *     the landed tile stays visually centred; otherwise it rests at
 *     `x=0`.
 *
 * Blur lives on its own object so the consumer can apply it to an
 * inner wrapper — motion fires `onAnimationComplete` per property,
 * and we want the lane translate to be the single source of truth.
 */
export function slotSpinAnimation({
  animMode,
  spinSteps,
  spinDuration,
  tileStep,
}: {
  animMode: AnimMode;
  spinSteps: number;
  spinDuration: number;
  tileStep: number;
}): SlotSpinAnimation {
  const totalDistance = spinSteps * tileStep;
  const overshootX = -(totalDistance + tileStep * OVERSHOOT_FRACTION);
  const finalX = -totalDistance;

  const isSpinning = animMode === 'spin';
  const restX =
    animMode === 'cleared' || animMode === 'reveal' ? finalX : 0;

  const laneAnimate = isSpinning
    ? { x: [0, overshootX, finalX] }
    : { x: restX };
  const laneTransition: Transition = isSpinning
    ? {
        duration: spinDuration / 1000,
        times: [0, 1 - REBOUND_T, 1],
        ease: [SLOT_CURVE, REBOUND_EASE],
      }
    : { duration: 0 };

  const blurAnimate = isSpinning
    ? {
        filter: [
          'blur(0px)',
          `blur(${BLUR_PEAK_PX}px)`,
          `blur(${BLUR_PEAK_PX}px)`,
          'blur(0px)',
        ],
      }
    : { filter: 'blur(0px)' };
  const blurTransition: Transition = isSpinning
    ? {
        duration: spinDuration / 1000,
        times: [0, 0.15, 0.78, 1],
        ease: 'easeInOut',
      }
    : { duration: 0 };

  return { laneAnimate, laneTransition, blurAnimate, blurTransition };
}
